import * as shiki from 'shiki';
import { CODE_LANGUAGES } from "@/editorjs/utils";

export default class ShikiCodeTool {
  static get toolbox() {
    return {
      title: 'ShikiCodeTool',
      icon: `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M7 8l-4 4l4 4" />
          <path d="M17 8l4 4l-4 4" />
          <path d="M14 4l-4 16" />
        </svg>
      `,
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, readOnly }) {
    this.data = {
      code: data.code || '',
      language: data.language || '',
      theme: data.theme || 'github-light',
      highlighted_html: data.highlighted_html || '',
    };
    this.readOnly = readOnly;
    this.wrapper = undefined;
    this.preview = undefined;
    this.textarea = undefined;
    this.selectedIndex = 0;
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('shiki-code-tool');

    this.preview = document.createElement('div');
    this.preview.className = 'shiki-preview'

    // Language input & dropdown
    if (this.readOnly) {
      this.wrapper.appendChild(this.preview);
      this.updatePreview();

      return this.wrapper;
    }

    this.languageInput = document.createElement('input');
    this.languageInput.type = 'text';
    this.languageInput.placeholder = 'Search language';
    this.languageInput.value = this.data.language;
    this.languageInput.classList.add('shiki-input');

    this.languageDropdown = document.createElement('div');
    this.languageDropdown.classList.add('shiki-dropdown', 'hidden');

    const languages = CODE_LANGUAGES.sort();

    const updateLanguageDropdown = () => {
      const input = this.languageInput.value.toLowerCase();
      this.languageDropdown.innerHTML = '';
      this.selectedIndex = 0;

      languages.filter(lang => lang.includes(input)).forEach((lang, index) => {
        const item = document.createElement('div');
        item.textContent = lang;
        item.classList.add('shiki-dropdown-item');
        item.addEventListener('click', () => {
          selectLanguage(lang);
        });

        this.languageDropdown.appendChild(item);
      });

      if (this.languageDropdown.children.length > 0) {
        this.languageDropdown.classList.remove('hidden');
      } else {
        this.languageDropdown.classList.add('hidden');
      }

      highlightDropdownItem(0);
    };

    const selectLanguage = (lang) => {
      this.languageInput.value = lang;
      this.data.language = lang;
      this.languageDropdown.classList.add('hidden');
      this.updatePreview();
    };

    const highlightDropdownItem = () => {
      Array.from(this.languageDropdown.children).forEach((item, index) => {
        item.classList.toggle('highlighted', index === this.selectedIndex);
      });
    };

    this.languageInput.addEventListener('input', updateLanguageDropdown);
    this.languageInput.addEventListener('focus', updateLanguageDropdown);

    this.languageInput.addEventListener('keydown', (e) => {
      const items = this.languageDropdown.children;

      if (e.key === 'ArrowDown') {
        this.selectedIndex = (this.selectedIndex + 1) % items.length;
        e.preventDefault();
        highlightDropdownItem();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.selectedIndex = (this.selectedIndex - 1 + items.length) % items.length;
        highlightDropdownItem();
      } else if (e.key === 'Enter' && this.selectedIndex >= 0) {
        e.preventDefault();
        selectLanguage(items[this.selectedIndex].textContent);
      }

      e.stopPropagation();
    });

    document.addEventListener('click', (e) => {
      if (!this.languageInput.contains(e.target) && !this.languageDropdown.contains(e.target)) {
        this.languageDropdown.classList.add('hidden');
      }
    });

    // Theme select
    this.selectTheme = document.createElement('select');
    this.selectTheme.name = "theme";
    const themes = [
      'github-dark-default', 'github-light', 'vitesse-dark', 'vitesse-light', 'dark-plus', 'light-plus', 'nord', 'dracula',
      'monokai', 'rose-pine' ,'rose-pine-dawn', 'min-dark', 'min-light', 'tokyo-night',
    ].sort();
    themes.forEach(theme => {
      const option = document.createElement('option');
      option.value = theme;
      option.textContent = theme;
      if (theme === this.data.theme) option.selected = true;
      this.selectTheme.appendChild(option);
    });
    this.selectTheme.addEventListener('change', () => {
      this.data.theme = this.selectTheme.value;
      this.updatePreview();
    });
    this.selectTheme.classList.add('shiki-select');

    // Container for selects
    this.selectContainer = document.createElement('div');
    this.selectContainer.appendChild(this.languageInput);
    this.selectContainer.appendChild(this.languageDropdown);
    this.selectContainer.appendChild(this.selectTheme);
    this.selectContainer.className = 'flex space-x-2 mb-2';

    this.shikiContainer = document.createElement('div');
    this.shikiContainer.className = 'shiki-container';

    // Code textarea
    this.textarea = document.createElement('textarea');
    this.textarea.placeholder = 'Enter your code here...';
    this.textarea.className = 'shiki-textarea';
    this.textarea.name = "code"
    this.textarea.spellcheck = false;
    this.textarea.value = this.data.code;
    this.textarea.readOnly = this.readOnly
    this.textarea.addEventListener('input', (e) => {
      let text = this.textarea.value;

      if(text[text.length-1] === "\n") {
        text += " ";
      }

      this.data.code = text;

      this.updatePreview();
    });

    this.textarea.addEventListener('keydown', (e) => {
      const element = e.target;
      let code = element.value;

      if (e.key === "Enter") {
        e.stopPropagation();  // Editor.js 블록 전환 방지
      }

      if (e.key === "Tab") {
        /* Tab key pressed */
        e.stopPropagation();  // Editor.js 블록 전환 방지
        e.preventDefault(); // stop normal
        let before_tab = code.slice(0, element.selectionStart); // text before tab
        let after_tab = code.slice(element.selectionEnd, element.value.length); // text after tab
        let cursor_pos = element.selectionStart + 1; // where cursor moves after tab - moving forward by 1 char to after tab
        element.value = before_tab + "\t" + after_tab; // add tab char
        // move cursor
        element.selectionStart = cursor_pos;
        element.selectionEnd = cursor_pos;

        this.data.code = element.value;
      }

      this.updatePreview();
    });


    this.textarea.addEventListener('scroll', this.syncScroll);

    this.shikiContainer.appendChild(this.textarea);
    this.shikiContainer.appendChild(this.preview);

    this.wrapper.appendChild(this.selectContainer);
    this.wrapper.appendChild(this.shikiContainer);

    this.updatePreview();

    return this.wrapper;
  }

  async save() {
    const { code, language, theme } = this.data;
    if (!code.trim()) {
      return {
        code: '',
        theme: '',
        language: '',
        highlighted_html: '',
      };
    }
    const highlighted_html = await shiki.codeToHtml(code, {
      lang: language,
      theme,
    });

    return {
      code,
      theme,
      language,
      highlighted_html,
    };
  }

  async updatePreview() {
    const { code, language, theme } = this.data;

    const highlighted_html = await shiki.codeToHtml(code, {
      lang: language,
      theme,
    });

    this.preview.innerHTML = highlighted_html;
    if (!this.readOnly) {
      this.syncScroll();
    }
  }

  syncScroll = () => {
    const pre = this.preview.querySelector('.shiki');

    if (pre) {
      pre.scrollLeft = this.textarea.scrollLeft;
      pre.scrollTop = this.textarea.scrollTop;
    }
  }

  validate(savedData) {
    return savedData.code.trim() !== '';
  }
}
